/**
 * Investor Pitch Deck Analysis
 *
 * Hook and utilities for investors to analyze pitch decks and add startups to dealflow.
 * Creates "unclaimed" startup profiles from extracted data.
 */

import { useState, useCallback, useRef } from 'react';
import { CLIENT_TIMEOUT_CODE } from '@/lib/supabase/edge-timeout';
import { supabase } from '@/lib/supabase/client';
import {
  buildAnalyzeRequest,
  defaultUploadDeps,
  uploadDeckForAnalysis,
} from '@/lib/investor/deck-upload';
import {
  analyzeInvestorPitchDeck,
  clearInflightRequest,
} from '@/lib/supabase/edge-functions';

/**
 * Ids for an analysis. `crypto.randomUUID` is present on Hermes and modern web;
 * the fallback keeps the uuid SHAPE because `deck_analyses.id` is a uuid column
 * and a non-uuid would be rejected at insert.
 */
function generateAnalysisId(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// The in-session base64 cache is GONE.
//
// It existed because the deck only ever lived in the client's memory — the
// analysis path never stored it. Decks now land in the `investor-decks` bucket,
// so the preview reads a signed URL instead (`deckPreviewUrl`): it survives a
// reload, works on another device, and does not pin ~25 MB of base64 in JS
// memory for the life of the session.

/**
 * Short-lived signed URL for a previously analysed deck, or null when the
 * company has no stored deck.
 *
 * Keyed on the startup rather than a local deal id: the deal card is a client
 * object, while `deck_analyses` is the durable record of what was uploaded.
 */
export async function deckPreviewUrl(startupId: string): Promise<string | null> {
  const { supabase } = await import('@/lib/supabase/client');

  const { data: analysis, error } = await supabase
    .from('deck_analyses')
    .select('deck_bucket, deck_path')
    .eq('startup_id', startupId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !analysis) return null;

  const { data: signed } = await supabase.storage
    .from(analysis.deck_bucket)
    .createSignedUrl(analysis.deck_path, 300);

  return signed?.signedUrl ?? null;
}

// =============================================================================
// Types
// =============================================================================

/** The deck's MIME type from its file name (PDF when unknown). Shared with the confirmed retry. */
export function deckMimeType(fileName: string | null | undefined): string {
  const extension = fileName?.split('.').pop()?.toLowerCase() || 'pdf';
  return extension === 'pdf' ? 'application/pdf' :
    extension === 'pptx' ? 'application/vnd.openxmlformats-officedocument.presentationml.presentation' :
    extension === 'ppt' ? 'application/vnd.ms-powerpoint' :
    extension === 'key' ? 'application/x-iwork-keynote-sffkey' :
    'application/pdf';
}

export interface AnalyzePitchDeckInput {
  /** File URI (from document picker) */
  fileUri?: string;
  /** File name */
  fileName?: string;
  /** URL to pitch deck (Google Drive, DocSend, etc.) */
  url?: string;
}

export interface AnalyzePitchDeckResult {
  success: boolean;
  /**
   * The analysis that was started. Company, score and pipeline card all arrive
   * later via `deck_analyses` — this call only gets the work accepted.
   */
  analysisId?: string;
  /**
   * True when the server accepted the job. The investor can leave the screen;
   * the pipeline card tracks it from here.
   */
  queued?: boolean;
  /** Error message if failed */
  error?: string;
  /**
   * We could not confirm the job was recorded. NOT a failure — the pipeline
   * card is the source of truth. Callers should show `message`, not an error.
   */
  stillProcessing?: boolean;
  /** User-facing explanation for a non-error, non-success outcome. */
  message?: string;
}

export interface UseAnalyzePitchDeckReturn {
  /** Analyze a pitch deck and add to dealflow */
  analyze: (input: AnalyzePitchDeckInput) => Promise<AnalyzePitchDeckResult>;
  /** Whether analysis is in progress */
  isAnalyzing: boolean;
  /** Current analysis stage */
  /**
   * `timeout` is distinct from `error`: the client stopped waiting, but the
   * edge function keeps running and commits. Treating it as an error told users
   * an analysis had failed when it had in fact succeeded.
   */
  /**
   * Only covers what THIS screen does: read the file, upload it, get the job
   * accepted. The old union carried 'extracting' / 'scoring' / 'complete',
   * which the client could never actually observe — it was guessing at server
   * progress from a progress bar it advanced itself. Those states now live on
   * the pipeline card, driven by `deck_analyses`.
   */
  stage: 'idle' | 'uploading' | 'queued' | 'error' | 'cancelled' | 'timeout';
  /** Progress percentage (0-100) */
  progress: number;
  /** Error message if failed */
  error: string | null;
  /** Cancel in-flight analysis */
  cancel: () => void;
  /** Reset state */
  reset: () => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

// NOTE: `richExtractionToScoreInput` was DELETED (2026-07-30).
//
// It shaped the extraction for the `ai-score` edge function, which this flow
// called as its second step. That endpoint is founder-only — it requires the
// `startup` role and then scores the CALLER's own startup — so an investor
// received 403 every time and the deal surfaced unscored. Scoring now happens
// server-side inside `investor-analyze-deck`, against the company it privately
// promotes first, so no client-side score input is built at all.

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook for analyzing pitch decks as an investor
 *
 * @example
 * ```typescript
 * const { analyze, isAnalyzing, stage, progress } = useAnalyzePitchDeck();
 *
 * const handleAnalyze = async () => {
 *   const result = await analyze({ fileUri: file.uri, fileName: file.name });
 *   if (result.success) {
 *     console.log('Added to dealflow:', result.dealflowId);
 *     router.push(`/startup-detail?id=${result.dealflowId}`);
 *   }
 * };
 * ```
 */
export function useAnalyzePitchDeck(): UseAnalyzePitchDeckReturn {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [stage, setStage] = useState<UseAnalyzePitchDeckReturn['stage']>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lastFileNameRef = useRef<string | undefined>(undefined);


  const analyze = useCallback(
    async (input: AnalyzePitchDeckInput): Promise<AnalyzePitchDeckResult> => {
      if (!input.fileUri && !input.url) {
        return { success: false, error: 'No file or URL provided' };
      }

      // Abort any previous in-flight analysis
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      lastFileNameRef.current = input.fileName;

      // Clear any stale dedupe entry for this file so retries aren't blocked
      clearInflightRequest('investor-analyze-deck', input.fileName);

      setIsAnalyzing(true);
      setError(null);
      setProgress(0);
      setStage('uploading');

      const throwIfCancelled = () => {
        if (controller.signal.aborted) throw new DOMException('Analysis cancelled', 'AbortError');
      };

      try {
        // ── Upload the deck, then hand over a pointer ────────────────────
        //
        // THIS IS THE FIX. The deck used to be base64-encoded into the request
        // body: an 18.5 MB file became a ~24.7 MB string, and between the raw
        // body, the parsed string, the decoded bytes and the AV scanner's copy
        // the edge isolate blew its 256 MB ceiling and was killed — no error,
        // no company, nothing in the logs but a CORS preflight (Octotronic,
        // 2026-08-01). The same payload was then sent a SECOND time to
        // `index-document` for RAG indexing.
        //
        // Uploading to Storage and sending an id makes the request a few
        // hundred bytes regardless of deck size, so the transport can no longer
        // be what kills the analysis.
        setProgress(10);

        const { data: authData } = await supabase.auth.getUser();
        const ownerId = authData.user?.id;
        if (!ownerId) {
          throw new Error('You need to be signed in to analyse a deck.');
        }

        const mimeType = deckMimeType(input.fileName);

        // Minted here so the storage object and the job row share an id, which
        // is what makes a retry reuse the upload instead of orphaning a copy.
        const analysisId = generateAnalysisId();

        const t0 = Date.now();
        const ref = await uploadDeckForAnalysis(
          {
            fileUri: input.fileUri!,
            fileName: input.fileName || 'pitch-deck.pdf',
            mimeType,
            ownerId,
            requestId: analysisId,
          },
          defaultUploadDeps(),
        );
        if (__DEV__) console.log(`[investor-analyze] Deck uploaded in ${Date.now() - t0}ms`);

        setProgress(55);
        throwIfCancelled();

        // ── Ask the server to start ──────────────────────────────────────
        //
        // Returns as soon as the job is RECORDED (202). Extraction, promotion
        // and scoring run in a background task, so there is no long-held
        // request to time out and no result to wait for here.
        setStage('queued');
        const result = await analyzeInvestorPitchDeck(
          buildAnalyzeRequest(ref, input.fileName || 'pitch-deck.pdf', mimeType),
        );
        throwIfCancelled();

        if (result.code === CLIENT_TIMEOUT_CODE) {
          // Still possible — this is now only the ACK, so a timeout here means
          // we do not know whether the job was recorded. The pipeline card
          // resolves it either way: if the row exists it appears, and if it
          // does not, nothing is left half-done.
          setStage('timeout');
          setProgress(100);
          return {
            success: false,
            stillProcessing: true,
            message:
              'We could not confirm the analysis started. Check your pipeline in a moment — if it is not there, try again.',
          };
        }

        if (result.error || !result.data?.success) {
          throw new Error(result.error || result.data?.error || 'Analysis failed');
        }

        setProgress(100);
        setStage('queued');

        // The deal appears in the pipeline as an "Analysing" card and fills in
        // as the background task progresses. The investor is free to leave.
        return {
          success: true,
          analysisId: result.data.analysisId ?? analysisId,
          queued: true,
          message: 'Analysing your deck — it will appear in your pipeline shortly.',
        };
      } catch (err) {
        // Don't set error state if the analysis was explicitly cancelled
        if (controller.signal.aborted) {
          setStage('cancelled');
          return { success: false, error: 'Analysis cancelled' };
        }
        const errorMessage = err instanceof Error ? err.message : 'Analysis failed';
        setError(errorMessage);
        setStage('error');
        return { success: false, error: errorMessage };
      } finally {
        setIsAnalyzing(false);
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    },
    []
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    // Clear dedupe cache so retries aren't blocked by stale promises
    clearInflightRequest('investor-analyze-deck', lastFileNameRef.current);
    setIsAnalyzing(false);
    setStage('cancelled');
    setProgress(0);
    setError(null);
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    clearInflightRequest('investor-analyze-deck', lastFileNameRef.current);
    setIsAnalyzing(false);
    setStage('idle');
    setProgress(0);
    setError(null);
  }, []);

  return {
    analyze,
    isAnalyzing,
    stage,
    progress,
    error,
    cancel,
    reset,
  };
}

// =============================================================================
// Exports
// =============================================================================

export type { StartupSummary, RichExtraction } from '@/lib/supabase/edge-functions';
